import styled from 'styled-components';
import { H3, H4, P, PSmall, LabelUppercase } from '../elements/Text';

export const FullScreenLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 80px);
  gap: 16px;
`;

export const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const PageTitle = styled(H3)`
  margin-top: 32px;
`;

export const PageLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 72px;
`;

export const Panel = styled.div`
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  border-radius: ${({ theme }) => theme.dimensions.sectionBorderRadius};
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const TableWrapper = styled.div<{ enableVirtualization?: boolean }>`
  flex: 1;
  min-height: 0;

  > div {
    height: 100%;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.colors.bgActive};

    > div:first-child {
      flex: 1;
      min-height: 0;
      border-radius: 8px;
    }
  }
`;

export const Divider = styled.div`
  height: 1px;
  width: 100%;
  background: ${({ theme }) => theme.colors.bgActive};
`;

export const SectionLabel = styled(LabelUppercase)`
  margin: 0;
  padding: 0 2px;
`;

export const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const SectionTitle = styled(H4)`
  margin: 0;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

export const SectionCaption = styled(PSmall)`
  margin: 0;
  color: ${({ theme }) => theme.colors.textPlaceholder};
`;

export const Card = styled.div<{ $disabled?: boolean }>`
  background: ${({ theme }) => theme.colors.bgDefaultSecondary};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  border-radius: 8px;
  padding: 20px;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  transition: all 200ms ease-in-out;
  display: flex;
  align-items: flex-start;
  gap: 12px;

  ${({ $disabled, theme }) =>
    !$disabled &&
    `
    &:hover {
      background: ${theme.colors.bgDefault};
    }
  `}
`;

export const CardIcon = styled.i`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.textPlaceholder};
  width: 22px;
  text-align: center;
  flex-shrink: 0;
  margin-top: 2px;
`;

export const CardTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

export const CardTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

export const CardSubtitle = styled.span`
  font-size: 13px;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.textRegular};
`;

export const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
`;

export const ToggleLabel = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textRegular};
`;

export const ToggleSwitch = styled.div`
  width: 40px;
  height: 22px;
  border-radius: 11px;
  background: ${({ theme }) => theme.colors.bgActive};
  cursor: pointer;
  position: relative;
  transition: background 0.2s;
  flex-shrink: 0;
`;

export const ToggleKnob = styled.div<{ $isOn: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${({ theme, $isOn }) =>
    $isOn ? theme.colors.primary : theme.colors.textPlaceholder};
  position: absolute;
  top: 2px;
  left: ${({ $isOn }) => ($isOn ? '20px' : '2px')};
  transition: left 0.2s, background 0.2s;
`;

export const StatusPillWrapper = styled.div`
  cursor: pointer;
  display: inline-flex;
`;

export const EmptyStateText = styled(P)`
  font-style: italic;
`;

export const ProgressTrack = styled.div`
  height: 6px;
  border-radius: 6px;
  background-color: ${({ theme }) => theme.colors.bgActive};
  overflow: hidden;
`;

export const ProgressFill = styled.div<{ $width: number; $color?: string }>`
  height: 100%;
  width: ${({ $width }) => $width}%;
  border-radius: 6px;
  background-color: ${({ $color, theme }) => $color ?? theme.colors.primary};
  transition: width 0.3s ease;
`;

export const MiniProgressTrack = styled.div`
  width: 100%;
  height: 4px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.bgActive};
  overflow: hidden;
`;

export const MiniProgressFill = styled.div<{ $width: number; $color?: string }>`
  height: 100%;
  width: ${({ $width }) => $width}%;
  border-radius: 4px;
  background-color: ${({ $color, theme }) => $color ?? theme.colors.primary};
  transition: width 0.3s ease;
`;

export const AssessmentCellWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  cursor: pointer;
  min-width: 0;
`;

export const AssessmentName = styled.span`
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ theme }) => theme.colors.textRegular};
`;
